import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { Loader2 } from 'lucide-react';

const WorkshopAnnouncements = () => {
  const { workshopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workshop, setWorkshop] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEnrolled, setIsEnrolled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Check if user is enrolled in this workshop
        const { data: enrollmentData, error: enrollmentError } = await supabase
          .from('workshop_enrollments')
          .select('id')
          .eq('user_id', user.id)
          .eq('workshop_id', workshopId)
          .single();

        if (enrollmentError && enrollmentError.code !== 'PGRST116') {
          throw enrollmentError;
        }

        setIsEnrolled(!!enrollmentData);

        // Fetch workshop details
        const { data: workshopData, error: workshopError } = await supabase
          .from('workshops')
          .select('*')
          .eq('id', workshopId)
          .single();

        if (workshopError) throw workshopError;
        setWorkshop(workshopData);

        // Fetch announcements for this workshop
        const { data: announcementsData, error: announcementsError } = await supabase
          .from('workshop_announcements')
          .select('*')
          .eq('workshop_id', workshopId)
          .order('created_at', { ascending: false });

        if (announcementsError) throw announcementsError;
        setAnnouncements(announcementsData || []);

      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load announcements');
      } finally {
        setLoading(false);
      }
    };

    if (workshopId) {
      fetchData();
    }
  }, [workshopId, user]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    if (diffInDays < 7) return `${diffInDays} days ago`;
    if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  if (!user) {
    return (
      <>
        <Helmet>
          <title>Workshop Announcements - UniSteps</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <main className="container mx-auto px-4 py-8 max-w-7xl pt-28">
            <div className="text-center py-12">
              <Icon name="Lock" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Authentication Required</h2>
              <p className="text-muted-foreground mb-6">
                Please log in to view workshop announcements.
              </p>
              <Button onClick={() => navigate('/login')}>
                Go to Login
              </Button>
            </div>
          </main>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{workshop?.title ? `${workshop.title} - Announcements` : 'Workshop Announcements'} - UniSteps</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="container mx-auto px-4 py-8 max-w-4xl pt-28">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate('/workshops-enrolled')}
            className="mb-6"
          >
            <Icon name="ArrowLeft" className="w-4 h-4 mr-2" />
            Back to Enrolled Workshops
          </Button>

          {/* Loading State */}
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-4 mb-6">
              <p className="text-destructive font-medium">{error}</p>
            </div>
          )}

          {/* Workshop Header */}
          {!loading && workshop && (
            <>
              <div className="glass-panel glass-stroke border border-border rounded-xl p-6 mb-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h1 className="text-3xl font-bold mb-2">{workshop.title}</h1>
                    {workshop.instructor && (
                      <p className="text-muted-foreground flex items-center gap-2">
                        <Icon name="User" size={16} />
                        {workshop.instructor}
                        {workshop.instructor_title && ` • ${workshop.instructor_title}`}
                      </p>
                    )}
                  </div>
                  {isEnrolled && (
                    <span className="px-3 py-1 bg-primary text-primary-foreground text-sm font-semibold rounded-full">
                      Enrolled
                    </span>
                  )}
                </div>
                
                {workshop.description && (
                  <p className="text-foreground mt-4">{workshop.description}</p>
                )}
              </div>

              {/* Announcements Section */}
              <div>
                <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
                  <Icon name="Bell" size={24} />
                  Announcements
                </h2>

                {/* Empty State */}
                {announcements.length === 0 && (
                  <div className="text-center py-12 glass-panel glass-stroke border border-border rounded-xl">
                    <Icon name="MessageSquare" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                    <h3 className="text-xl font-semibold mb-2">No Announcements Yet</h3>
                    <p className="text-muted-foreground">
                      Check back later for updates from your instructor.
                    </p>
                  </div>
                )}

                {/* Announcements List */}
                {announcements.length > 0 && (
                  <div className="space-y-4">
                    {announcements.map((announcement) => (
                      <div
                        key={announcement.id}
                        className="glass-panel glass-stroke border border-border rounded-xl p-6 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h3 className="text-xl font-semibold flex-1">
                            {announcement.title}
                          </h3>
                          <span className="text-sm text-muted-foreground ml-4">
                            {formatRelativeTime(announcement.created_at)}
                          </span>
                        </div>
                        
                        <div className="text-foreground whitespace-pre-wrap leading-relaxed">
                          {announcement.content}
                        </div>

                        <div className="mt-4 pt-4 border-t border-border">
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Icon name="Calendar" size={14} />
                            <span>{formatDate(announcement.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Workshop Not Found */}
          {!loading && !workshop && (
            <div className="text-center py-12">
              <Icon name="AlertCircle" className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-2xl font-bold mb-2">Workshop Not Found</h2>
              <p className="text-muted-foreground mb-6">
                The workshop you're looking for doesn't exist or has been removed.
              </p>
              <Button onClick={() => navigate('/workshops-enrolled')}>
                Back to Enrolled Workshops
              </Button>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default WorkshopAnnouncements;
